// src/components/CustomersFilters.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';

const CustomersFilters = ({ userLevel, privileges, setFilters, hasAccess }) => {
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluk, setSelectedTaluk] = useState('');
  const [selectedPincode, setSelectedPincode] = useState('');
  const [vpa, setVpa] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    if (userLevel === 1 || userLevel === 2) {
      fetchDistricts();
    } else if (userLevel === 3 || (userLevel > 3 && hasAccess())) {
      setPincodes(privileges.pincodes || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLevel, privileges]);

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      if (response.status === 200) {
        setDistricts(response.data.districts || response.data);
      } else {
        console.error('Failed to fetch districts');
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchTaluks = async (districtId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taluks/district/${districtId}`);
      if (response.status === 200) {
        setTaluks(response.data.taluks || response.data);
      } else {
        setTaluks([]);
        console.error('Failed to fetch taluks');
      }
    } catch (error) {
      setTaluks([]);
      if (error.response && error.response.status === 404) {
        console.error('No Constituency found for the given districtId');
      } else {
        console.error('Error fetching Constituency:', error);
      }
    }
  };

  const fetchPincodes = async (talukId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pincodes/taluk/${talukId}`);
      if (response.status === 200) {
        setPincodes(response.data.pincodes);
      } else {
        setPincodes([]);
        console.error('Failed to fetch pincodes');
      }
    } catch (error) {
      setPincodes([]);
      if (error.response && error.response.status === 404) {
        console.error('No pincodes found for the given talukId');
      } else {
        console.error('Error fetching pincodes:', error);
      }
    }
  };

  const handleDistrictChange = (event) => {
    const districtId = event.target.value;
    setSelectedDistrict(districtId);
    setSelectedTaluk('');
    setSelectedPincode('');
    setTaluks([]);
    setPincodes([]);

    // Fetch taluks for the selected district
    fetchTaluks(districtId);

    // Update filters, resetting talukId and pincode
    setFilters({
      districtId,
      talukId: null,
      pincode: null,
      vpa: null,
      phoneNumber: null,
    });
  };

  const handleTalukChange = (event) => {
    const talukId = event.target.value;
    setSelectedTaluk(talukId);
    setSelectedPincode('');
    setPincodes([]);

    // Fetch pincodes for the selected taluk
    fetchPincodes(talukId);

    // Update filters, resetting pincode
    setFilters((prevFilters) => ({
      ...prevFilters,
      talukId,
      pincode: null,
      vpa: null,
      phoneNumber: null,
    }));
  };

  const handlePincodeChange = (event) => {
    const pincode = event.target.value;
    setSelectedPincode(pincode);

    // Update filters
    setFilters((prevFilters) => ({
      ...prevFilters,
      pincode,
      vpa: null,
      phoneNumber: null,
    }));
  };

  const handleVpaChange = (event) => {
    const vpaInput = event.target.value;
    setVpa(vpaInput);

    // Update filters
    setFilters((prevFilters) => ({
      ...prevFilters,
      vpa: vpaInput || null,
      pincode: null,
      talukId: null,
      districtId: null,
      phoneNumber: null,
    }));
  };

  const handlePhoneNumberChange = (event) => {
    const phone = event.target.value;
    setPhoneNumber(phone);

    // Update filters
    setFilters((prevFilters) => ({
      ...prevFilters,
      phoneNumber: phone || null,
      vpa: null,
      pincode: null,
      talukId: null,
      districtId: null,
    }));
  };

  return (
    <Grid container spacing={2} alignItems="center" style={{ marginTop: '20px' }}>
      {(userLevel === 1 || userLevel === 2) && (
        <React.Fragment>
          {/* District Filter */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel id="district-label">District</InputLabel>
              <Select
                labelId="district-label"
                value={selectedDistrict}
                label="District"
                onChange={handleDistrictChange}
              >
                {districts.map((district) => (
                  <MenuItem key={district._id} value={district._id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Constituency Filter */}
          {selectedDistrict && (
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth disabled={taluks.length === 0}>
                <InputLabel id="taluk-label">CONSTITUENCY</InputLabel>
                <Select
                  labelId="taluk-label"
                  value={selectedTaluk}
                  label="CONSTITUENCY"
                  onChange={handleTalukChange}
                >
                  {taluks.length > 0 ? (
                    taluks.map((taluk) => (
                      <MenuItem key={taluk._id} value={taluk._id}>
                        {taluk.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">
                      <em>No constituency available</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Pincode Filter */}
          {selectedTaluk && (
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth disabled={pincodes.length === 0}>
                <InputLabel id="pincode-label">Pincode</InputLabel>
                <Select
                  labelId="pincode-label"
                  value={selectedPincode}
                  label="Pincode"
                  onChange={handlePincodeChange}
                >
                  {pincodes.length > 0 ? (
                    pincodes.map((pincode) => (
                      <MenuItem key={pincode} value={pincode}>
                        {pincode}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">
                      <em>No pincodes available</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
        </React.Fragment>
      )}
      {(userLevel === 3 || (userLevel > 3 && hasAccess())) && (
        <React.Fragment>
          {/* VPA Search */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel id="vpa-label">VPA Search</InputLabel>
              <Select
                labelId="vpa-label"
                value={vpa}
                label="VPA Search"
                onChange={handleVpaChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {/* If you have a list of VPAs, you can map them here */}
                {/* Otherwise, consider using a TextField for free input */}
                <MenuItem value="john@upi">john@upi</MenuItem>
                <MenuItem value="doe@upi">doe@upi</MenuItem>
                {/* Add more as needed */}
              </Select>
            </FormControl>
          </Grid>

          {/* Phone Number Search */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel id="phone-label">Phone Number</InputLabel>
              <Select
                labelId="phone-label"
                value={phoneNumber}
                label="Phone Number"
                onChange={handlePhoneNumberChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {/* If you have a list of phone numbers, you can map them here */}
                {/* Otherwise, consider using a TextField for free input */}
                <MenuItem value="9142125724">9142125724</MenuItem>
                <MenuItem value="9876543210">9876543210</MenuItem>
                {/* Add more as needed */}
              </Select>
            </FormControl>
          </Grid>
        </React.Fragment>
      )}
    </Grid>
  );
};

export default CustomersFilters;
